/**
 * SSE hook for real-time transfer status updates.
 * Follows the same pattern as useUnifiedNotifications.tsx.
 */
import { useState, useEffect, useRef, useCallback } from 'react'

import { getToken } from '../utils/utils'

import { makeUrlTransfersSse } from './connectionData'

interface TransferUpdate {
  _id: string
  number: number
  status: string
  vehicleData?: { plate: string }
  updatedAt: string
  transfieroId?: string
}

interface TransferSseMessage {
  type: 'connected' | 'transfer_updates'
  transfers?: TransferUpdate[]
  timestamp: string
}

const useTransferUpdates = (organizationId: string | undefined) => {
  const [latestUpdates, setLatestUpdates] = useState<TransferUpdate[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const connect = useCallback(() => {
    if (!organizationId) return

    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const token = getToken()

    if (!token) return

    const url = `${makeUrlTransfersSse(organizationId)}?token=${token}`

    const eventSource = new EventSource(url, { withCredentials: true })
    eventSourceRef.current = eventSource

    eventSource.onopen = () => {
      setIsConnected(true)
    }

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as TransferSseMessage

        if (data.type === 'connected') {
          setIsConnected(true)
        } else if (data.type === 'transfer_updates' && data.transfers) {
          setLatestUpdates(data.transfers)
        }
      } catch {
        // Ignore parse errors (e.g., heartbeat comments)
      }
    }

    eventSource.onerror = () => {
      setIsConnected(false)
      eventSource.close()

      // Reconnect after 5 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connect()
      }, 5000)
    }
  }, [organizationId])

  useEffect(() => {
    connect()

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
    }
  }, [connect])

  return { latestUpdates, isConnected }
}

export default useTransferUpdates
